// ─── Types ───────────────────────────────────────────────────────────────────
export type {
  ProtocolType, ChainKey, ChainConfig, NPaymentConfig, GoatCredentials,
  PaymentAdapter, PaymentEvent, AnalyticsPlugin, PaywallRouteConfig, PaywallConfig,
  OWSConfig, BtcLendingConfig, XrplConfig, StellarConfig, MorphConfig, PaymentContext,
  SpaceRouterConfig, SpaceRouterRegion, SpaceRouterIpType, ProxyAdapter,
  AaveConfig, FlareConfig, FlareNetwork, FlareX402Config, FlareGaslessConfig,
  // v0.25 — Celo L2
  CeloConfig, CeloAgentVisaTier, AgentVisaState, AgentVisaStorage,
} from './types.js';
export type {
  BazaarResource, BazaarSearchResult,
  OffRampAdapter, OffRampQuoteParams, OffRampQuote, OffRampWithdrawParams, OffRampReceipt,
} from './types.js';

// ─── OWS ─────────────────────────────────────────────────────────────────────
// v0.27 — CAIP-2-native multichain wallet + full lifecycle facade.
// Back-compat: OWSWallet class name + signatures preserved exactly.
export { OWSWallet, OwsWallet, ows } from './ows/index.js';
export {
  FAMILY_TABLE, parseCaip2, resolveFamily, resolveSpec,
  getDerivationPath, getSlip44, extractEvmChainId, listSupportedNamespaces,
  createOWSDriver, listAvailableFamilies,
} from './ows/index.js';
export type {
  OWSSignResult, TransactionRequest, ChainFamily, FamilySpec, SignedTx,
  WalletDescriptor, WalletAccount, CreateWalletOpts, DiscoverOpts,
  PolicyDef, PolicyId, PolicySummary,
  ApiKeyId, ApiKeyOpts, ApiKeyIssued, ApiKeySummary,
  Signer, Lifecycle, PolicyManager, KeyManager, OWSDriver,
} from './ows/index.js';

// ─── Core ────────────────────────────────────────────────────────────────────
export { CHAINS, getChain, getChainsForProtocol } from './chains.js';
export { createConfig } from './config.js';
export { detectProtocol } from './detect.js';
export { NPaymentError, ChallengeParseError, InsufficientBalanceError, AdapterNotFoundError } from './errors.js';
export { ConsoleAnalytics, AnalyticsEmitter } from './analytics.js';

// ─── Client ──────────────────────────────────────────────────────────────────
export { PaymentClient, createPaymentClient } from './client.js';

// ─── Middleware ───────────────────────────────────────────────────────────────
export { createPaywall, createMppPaywall, createHealthEndpoint } from './middleware.js';

// ─── GOAT Network ────────────────────────────────────────────────────────────
export { GoatX402Client } from './goat/client.js';
export { GoatIdentity, GOAT_IDENTITY_REGISTRY, GOAT_REPUTATION_REGISTRY } from './goat/identity.js';
export { signGoatRequest } from './goat/auth.js';
export type { GoatOrder, GoatProof, GoatCreateOrderParams, GoatOrderStatus } from './goat/types.js';

// ─── BTC Lending ─────────────────────────────────────────────────────────────
export { BtcLendingVault } from './goat/lending.js';

// ─── GOAT USDC Acquisition Router (v0.17) ────────────────────────────────────
export {
  UsdcAcquisitionRouter,
  BalanceSheetStrategy,
  GoatAcquisitionPresets,
  GoatTokens,
  GoatBalances,
  GoatDexSwap,
  LayerZeroOftClient,
  BitVMBridgeClient,
  MockSwapAdapter,
  MockOftAdapter,
  MockBridgeAdapter,
} from './goat/acquisition.js';
export type {
  AcquireParams,
  AcquireResult,
  RoutingDecision,
  AcquisitionRoutingStrategy,
  RouterDeps,
  AcquisitionPathAdapter,
  AcquisitionQuote,
  AcquisitionReceipt,
  BalanceSheet,
} from './goat/acquisition.js';
export type {
  AcquisitionPath,
  GoatAcquisitionConfig,
  BtcSigner,
} from './types.js';
export { GOAT_ACQUISITION_HINTS, goatError } from './errors.js';

// ─── Bazaar (Discovery) ──────────────────────────────────────────────────────
export { BazaarClient, createBazaarClient, MOCK_CATALOG } from './bazaar/index.js';
export type { BazaarClientConfig } from './bazaar/index.js';

// ─── Off-Ramp ────────────────────────────────────────────────────────────────
export { OffRampClient, MockMoonPayAdapter } from './offramp/index.js';
export {
  StellarAnchorClient,
  DefaultAnchorRegistry,
  stellarAgentKit,
  type AnchorDescriptor,
  type AnchorRegistry,
  type AnchorServiceUrls,
  type OffRampInitiateInput,
  type OffRampHandle,
  type OffRampQuoteInput,
  type OffRampQuoteResult,
  type OffRampB2BInput,
  type OffRampB2BHandle,
} from './offramp/index.js';

// ─── Faucet ──────────────────────────────────────────────────────────────────
export { TestnetFaucet } from './faucet.js';

// ─── Stellar & Trustless Work ────────────────────────────────────────────────
export { StellarWallet } from './stellar/wallet.js';
export type { StellarWalletConfig } from './stellar/wallet.js';
export { TrustlessWorkClient } from './stellar/trustless-work.js';
export type { TrustlessWorkConfig, EscrowDeployParams, EscrowStatus } from './stellar/trustless-work.js';
export { TrustlessEscrowManager } from './stellar/escrow-manager.js';
export type { TrustlessJob, TrustlessJobStatus, TrustlessEscrowConfig } from './stellar/escrow-manager.js';
export { StellarX402Adapter } from './adapters/stellar-x402.js';
export { StellarMppAdapter } from './adapters/stellar-mpp.js';

// ─── Stellar Agentic Payments (v0.10) ────────────────────────────────────────
export { KeypairStellarSigner, FreighterStellarSigner } from './stellar/signer.js';
export type { StellarSigner } from './stellar/signer.js';
export { StellarChannelsClient } from './stellar/channels-client.js';
export type {
  StellarChannelsClientConfig, StellarSupportedKind, StellarSupportedResponse,
  StellarVerifyResponse, StellarSettleResponse,
} from './stellar/channels-client.js';
export { StellarSessionClient, StellarSessionServer } from './stellar/session.js';
export type {
  StellarSessionClientConfig, StellarSessionServerConfig, VoucherCredential,
} from './stellar/session.js';
export type { MppChargeMode } from './adapters/stellar-mpp.js';

// ─── Stellar v0.21 — MGUSD + multi-stable + KYA ─────────────────────────────
export {
  STELLAR_ASSETS,
  STELLAR_ASSET_SYMBOLS,
  MGUSD_ISSUER_PLACEHOLDER,
  getStellarAsset,
  parseStellarAsset,
  formatStellarAsset,
  assertVerifiedIssuer,
  type StellarAsset,
  type StellarAssetSymbol,
} from './stellar/assets.js';
export {
  selectAsset,
  findStellarAmmPath,
  CORRIDOR_PREFERENCE,
  type Corridor,
  type SelectAssetInput,
  type SelectAssetResult,
  type StellarAmmPath,
} from './stellar/asset-router.js';
export {
  StellarKyaGate,
  StellarKyaCredentialFetcher,
  attachKyaIfRequired,
  canonicalCredentialBytes,
  type KyaCredential,
  type KyaGateConfig,
} from './stellar/kya.js';

// ─── XRPL (Ripple) ──────────────────────────────────────────────────────────
export { XrplClient, createXrplClient } from './xrpl/client.js';
export type { XrplClientConfig, XrplHealthReport } from './xrpl/client.js';
export { XrplWallet } from './xrpl/wallet.js';
export { XrplConnection } from './xrpl/connection.js';
export { XrplVaultClient } from './xrpl/vault.js';
export type { VaultCreateOptions, VaultInfo } from './xrpl/vault.js';
export { DiaOracleClient } from './xrpl/oracle.js';
export type { OraclePrice } from './xrpl/oracle.js';
export {
  ensureTrustLine, ensureTrustline, clearTrustlineCache,
  sendRLUSD, getRLUSDBalance, readAccountState, clearAccountStateCache,
} from './xrpl/payments.js';
export type { AccountState, IssuerOpts, TrustlineState, EnsureTrustlineOpts } from './xrpl/payments.js';
export { XrplAdapter } from './adapters/xrpl.js';
export type { XrplAdapterOptions } from './adapters/xrpl.js';
// ─── XRPL v0.14 ─────────────────────────────────────────────────────────────
export {
  RLUSD_ISSUERS, RLUSD_CURRENCY, RLUSD_HEX, RLUSD_SYMBOL, RLUSD_DECIMALS,
  getRlusdIssuer, networkFromChainKey,
  parseRlusdAmount, formatRlusdAmount,
  padSendMaxDrops, computeQuoteSlippageBps,
} from './xrpl/utils.js';
export type { XrplNetwork } from './xrpl/utils.js';
export { XrplSwapClient } from './xrpl/swap.js';
export type {
  XrplSwapQuote, XrplSwapQuoteOptions, XrplSwapOptions, XrplSwapResult, SwapAsset,
} from './xrpl/swap.js';
export { XrplTreasuryManager, MemoryVaultIdStore } from './xrpl/treasury.js';
export type {
  VaultIdStore, XrplTreasuryConfig, XrplTreasuryDeps, XrplTreasuryState,
} from './xrpl/treasury.js';
export type { XrplTreasuryConfigInput } from './types.js';

// ─── XRPL x402 facilitator (T54 scheme) ─────────────────────────────────────
// The merchant-side wire codecs + facilitator HTTP client for the XRPL exact
// scheme. Compiled since v0.22 but previously unexported — surfacing them lets
// merchants (e.g. HyperMove) emit PAYMENT-REQUIRED and settle via /verify+/settle.
export {
  XrplFacilitatorClient,
  defaultFacilitatorUrl,
  hexInvoiceMemo,
  encodePaymentRequiredHeader,
  decodePaymentRequiredHeader,
  encodePaymentSignatureHeader,
  decodePaymentSignatureHeader,
  encodePaymentResponseHeader,
  decodePaymentResponseHeader,
} from './xrpl/x402-scheme.js';
export type {
  XrplPaymentRequirements,
  PaymentRequiredEnvelope,
  PaymentSignatureEnvelope,
  PaymentResponseEnvelope,
  FacilitatorVerifyInput,
  FacilitatorVerifyResult,
  FacilitatorSettleResult,
} from './xrpl/x402-scheme.js';

// ─── Transactor ──────────────────────────────────────────────────────────────
export { ViemTransactor } from './transactor.js';
export type { TransactionResult } from './transactor.js';

// ─── Agent Commerce (v0.5) ─────────────────────────────────────────────────
export {
  paidTool, AgentProvider, createAgentProvider,
  AgentClient, createAgentClient,
  PricingEngine, DemandStrategy, ReputationStrategy, OutcomeStrategy,
  SessionManager, EscrowManager, PaymentNegotiator,
  ReputationRouter, DelegationManager, AgentCard,
} from './agent/index.js';

export type {
  PricingMode, PricingStrategy, PricingContext, PricingConfig,
  Session, SessionConfig, Job, JobStatus, EscrowConfig,
  PaymentTerms, NegotiationResult, NegotiationPolicy,
  DelegationContext, DelegationConfig,
  AgentSkill, AgentCardData, PaidToolDef, ToolCallContext,
  AgentProviderConfig, AgentClientConfig,
  RoutingStrategy, ProviderCandidate, RouterConfig,
} from './agent/index.js';

// ─── Settlement (v0.8) ───────────────────────────────────────────────────────
export {
  BatchSettlementManager, StreamingPaymentManager, Permit2Signer,
} from './settlement/index.js';

export type {
  BatchSessionConfig, BatchVoucher, BatchSession,
  StreamConfig, StreamSession, Permit2Params,
} from './settlement/index.js';

// ─── Policy (v0.8) ──────────────────────────────────────────────────────────
export {
  PolicyEngine, AuditLog, SpendingGuard,
} from './policy/index.js';

export type {
  PolicyRule, PolicyDecision, PaymentRequest, AuditEntry, PolicyConfig,
} from './policy/index.js';

// ─── AP2 Protocol (v0.8) ────────────────────────────────────────────────────
export {
  AP2Client, VerifiableIntentSigner,
} from './ap2/index.js';

export type {
  AP2Config, CheckoutMandate, PaymentMandate, VerifiableIntent,
  MandateConstraints, CartDetails,
} from './ap2/index.js';

// ─── Circle Gateway (v0.8) ──────────────────────────────────────────────────
export { CircleGatewayAdapter } from './adapters/circle-gateway.js';
export type { CircleGatewayConfig } from './adapters/circle-gateway.js';

// ─── Solana x402 (v0.8) ─────────────────────────────────────────────────────
export { SolanaX402Adapter } from './adapters/solana-x402.js';
export type { SolanaX402Config } from './adapters/solana-x402.js';

// ─── Morph Network (v0.9) ───────────────────────────────────────────────────
export { MorphX402Adapter } from './adapters/morph-x402.js';
export { MorphX402Client } from './morph/client.js';
export { signMorphRequest, sortObjectDeep } from './morph/auth.js';
export type {
  MorphX402ClientConfig, MorphSupportedKind, MorphSupportedResponse,
  MorphVerifyResponse, MorphSettleResponse,
} from './morph/client.js';
export type { MorphSignParams } from './morph/auth.js';

// ─── Morph Hoodi Facilitator (v0.18) ────────────────────────────────────────
export { createMorphHoodiFacilitator } from './morph/facilitator.js';
export type { MorphHoodiFacilitatorConfig } from './morph/facilitator.js';
export {
  buildTransferWithAuthorizationTypedData,
  randomEip3009Nonce,
  encodeAuthorizationPayload,
  decodeAuthorizationPayload,
  splitSignature,
  TRANSFER_WITH_AUTHORIZATION_TYPES,
  EIP3009_ABI,
} from './morph/eip3009.js';
export type {
  TransferAuthorization, AuthorizationPayload, BuildTypedDataInput, SignableTypedData,
} from './morph/eip3009.js';

// ─── SpaceRouter / SpaceCoin (v0.11) ────────────────────────────────────────
export type {
  SpaceRouterSigner, SpaceRouterReceipt,
  SpaceRouterClientConfig, RoutedResponse,
  EscrowClientConfig, WithdrawalRequest,
  GatewayChallenge, GatewayClientConfig, ReceiptSchedulerConfig,
  SignedReceiptEnvelope, SyncResult,
} from './spacerouter/index.js';
export {
  SpaceRouterClient, SpaceRouterAdapter, SpaceRouterPeerDepMissingError,
  SpaceRouterEscrowClient, SpaceRouterGatewayClient, SpaceRouterReceiptScheduler,
  KeypairSpaceRouterSigner, OWSSpaceRouterSigner, BrowserSpaceRouterSigner,
  EscrowEmptyError, WithdrawalLockedError, WithdrawalAlreadyPendingError,
  InvalidAmountError as SpaceRouterInvalidAmountError,
  parseSpace, formatSpace, SPACE_DECIMALS,
  spaceRouterDomain, SPACEROUTER_RECEIPT_TYPES,
} from './spacerouter/index.js';

// ─── Aave Treasury (v0.13) ──────────────────────────────────────────────────
export {
  AaveTreasuryManager, AaveClient, YieldManager, GhoManager,
  VaultManager, FlashMintBatcher, DelegationBridge,
  GHO_ADDRESSES, AAVE_POOL_ADDRESSES, GHO_FLASH_MINTER,
} from './aave/index.js';

export type {
  YieldState, VaultConfig, VaultState,
  FlashPayment, FlashBatchResult, Delegation,
} from './aave/index.js';

export { AaveGhoAdapter } from './adapters/aave-gho.js';

// ─── Flare FXRP Bridge (v0.15) + x402 + Gasless (v0.19) ────────────────────
export {
  FlareClient, FlareContractsRegistry, createFlareClient, FLARE_CONTRACT_REGISTRY_ADDRESS,
  getPersonalAccountAddress, isSmartAccount, getOperatorXrplAddresses,
  getFxrpAddress, getFxrpBalance, getFxrpDecimals, getVaults, getAgentVaults,
  parseXrpDropsAmount, formatXrpDropsAmount, computeDirectMintingQuote,
  encodeDirectMintingMemo32, toXrplMemoHex,
  getDirectMintingFees, getDirectMintingPaymentAddress, preflightDirectMintingLimits,
  XRP_SCALE, DIRECT_MINTING_MEMO_PREFIX,
  FlareBridgeClient, createFlareBridgeClient,
  // v0.19
  FlareX402Adapter, verifyAndSettleFlareX402, decodeFlareX402Header,
  buildFlareX402Challenge, deployFlareX402Contracts, X402_FACILITATOR_ABI,
  FlareGaslessForwarderClient, createGaslessExecutor, deployFlareGaslessForwarder,
  PAYMENT_REQUEST_TYPES, FORWARDER_ABI,
  // v0.22.1 — FAssets redemption (XRPFi reverse leg)
  computeRedemptionQuote, getRedemptionFees, getLotSize,
} from './flare/index.js';
export type {
  FlareClientConfig, FlareContractName,
  FlareVault, FlareAgentVault,
  DirectMintingFees, DirectMintingQuote, DirectMintingPreflight,
  FlareBridgeConfig, FlareMintParams, FlareMintReceipt,
  // v0.19
  FlareX402Payload, VerifyAndSettleParams, VerifyAndSettleResult,
  ContractArtifact, DeployFlareX402Params, DeployFlareX402Result,
  FlareGaslessClientConfig, FlarePaymentRequest, FlareGaslessStatus,
  FlareGaslessExecuteResult, GaslessRelayerHandlerConfig, DeployFlareGaslessParams,
  // v0.22.1 — FAssets redemption (XRPFi reverse leg)
  RedemptionFees, RedemptionQuote,
  FlareRedeemParams, FlareRedeemReceipt, FlareRedemptionStatus, PollRedemptionOptions,
} from './flare/index.js';

// ─── Flare merchant paywall deps (v0.19) ────────────────────────────────────
export type { FlareMerchantDeps } from './middleware.js';

// ─── Wormhole NTT (v0.22) ───────────────────────────────────────────────────
export {
  RLUSD_NTT_DEPLOYMENTS,
  CHAIN_KEY_TO_WH,
  whChainFromKey,
  chainKeyFromWh,
  canBridgeRlusd,
  WormholeNttClient,
  createDefaultWormholeNttBridgeFactory,
} from './wormhole/index.js';
export { WormholeNttAdapter } from './adapters/wormhole-ntt.js';
export type {
  WormholeChainName,
  NttDeployment,
  CanBridgeResult,
  EvmSigner,
  NttBridgeRequest,
  NttBridgeResult,
  NttTransferRequest,
  NttTransferReceipt,
  WormholeNttBridge,
  WormholeNttBridgeFactory,
  WormholeNttClientConfig,
  WormholeNttAdapterOptions,
} from './wormhole/index.js';
export type { WormholeConfig } from './types.js';
export type { XrpfiConfig } from './types.js';

// ─── PayRouter v3 — RLUSD corridor (v0.22) ──────────────────────────────────
export { selectRlusdCorridor } from './payrouter/index.js';
export type { CorridorRouteInput, CorridorRouteDecision } from './payrouter/index.js';

// ─── Initia × iUSD bridge corridor (v0.23) ──────────────────────────────────
export {
  INITIA_ASSETS,
  getInitiaAsset,
  assertVerifiedDenom,
  parseInitiaAmount,
  formatInitiaAmount,
  InitiaClient,
  mnemonicSigner,
  selectIusdCorridor,
  SkipApiClient,
  LayerZeroAusdClient,
  IusdBridgeOrchestrator,
} from './initia/index.js';
export type {
  InitiaAsset,
  InitiaAssetSymbol,
  InitiaConfig,
  InitiaNetwork,
  InitiaChainKey,
  IusdConfig,
  IusdCorridor,
  IusdCorridorStep,
  IusdCorridorResult,
  IusdCorridorInput,
  SkipQuoteRequest,
  SkipQuoteResponse,
  InitiaClientConfig,
  InitiaSigner,
  BroadcastResult,
  SkipApiClientConfig,
  LayerZeroAusdClientConfig,
  IusdBridgeOrchestratorConfig,
} from './initia/index.js';
export { InitiaIusdAdapter } from './adapters/initia-iusd.js';
export type { InitiaIusdAdapterOptions } from './adapters/initia-iusd.js';

// ─── RLUSD facilitator-independent settlement (v0.22 PRD-D) ────────────────
export { RlusdExactAdapter } from './adapters/rlusd-exact.js';
export {
  verifyExactRlusdPayment,
  verifyWormholeNttPayment,
  decodeRlusdExactPayment,
  _clearRlusdIdempotencyCache,
} from './middleware.js';
export type {
  RlusdRpcClient,
  VerifyRlusdExactInput,
  VerifyRlusdResult,
  VerifyWormholeNttInput,
} from './middleware.js';

// ─── Celo CIP-64 fee abstraction + Mento + Agent Visa (v0.25) ───────────────
export {
  CeloFeeAbstractedTransactor,
  TRANSFER_WITH_AUTHORIZATION_ABI,
  type CeloChainKey,
  type CeloPayAsset,
  type CeloFeeAbstractedTransactorOptions,
  MENTO_ASSETS,
  selectMentoCorridor,
  type MentoAssetSymbol,
  type MentoAsset,
  type MentoCorridorInput,
  type MentoCorridorDecision,
  type MentoCorridorLeg,
  MentoBrokerClient,
  type MentoBrokerClientOptions,
  CeloAgentVisaTracker,
  computeAgentVisaTier,
  MemoryAgentVisaStorage,
  JsonFileAgentVisaStorage,
  type CeloAgentVisaTrackerOptions,
} from './celo/index.js';
export { CeloFeeAbstractedAdapter } from './adapters/celo-fee-abstracted.js';
export type {
  CeloEip3009Authorization,
  CeloSettleResult,
} from './adapters/celo-fee-abstracted.js';
